import {
  Box,
  Card,
  CardContent,
  Chip,
  IconButton,
  Tooltip,
  Typography,
  useTheme,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import Edit from "@mui/icons-material/Edit";
import { memo, useState } from "react";
import WorkOrderCard from "components/QueueBoard/work-order-dashboard-card/WorkOrderDetail";
import { BoardView } from "../types/boardView";
import { DragDropContext, Droppable, Draggable } from "@hello-pangea/dnd";
import { DropResult } from "@hello-pangea/dnd";
import {
  UpdateWorkOrderPriority,
  WorkOrdersByPriority,
} from "../models/workOrderTypes";
import {
  convertToRGBA,
  truncateName,
} from "../../common/Utils/commonUtilityFunctions";
import WorkOrderDialogContainer from "components/workOrder/WorkOrderDialogContainerMain";
import { WorkOrderQueueAPI } from "../api/workOrderApi";
import { ConcurrencyError } from "../../common/Utils/concurrencyError";
import { ConcurrencyDialog } from "../../common/components/ConcurrencyDialog";
import {
  useBoardFilters,
  useBoardLoading,
  usePriorityBoardData,
} from "../../store/zustandWorkOrderStore";
import { useBoardColumns } from "../shared/hooks/useBoardColumns";
import { useCachedWorkOrderOpen } from "../shared/hooks/useCachedWorkOrderOpen";
import BoardLayout from "../shared/components/BoardLayout";
import PriorityBoardCustomColumns from "./PriorityBoardCustomColumns";

const PriorityBoard = memo(function PriorityBoard() {
  const theme = useTheme();
  const loading = useBoardLoading();
  const filters = useBoardFilters();
  const WorkOrdersByPriority = usePriorityBoardData();
  const [columns, setColumns] = useBoardColumns<WorkOrdersByPriority>(
    WorkOrdersByPriority,
    "priorityPosition",
    { thenByCreatedDateAsc: true },
  );
  const { showDialog, selectedWorkOrderId, showHideWorkOrderPopup } =
    useCachedWorkOrderOpen({
      loading,
      hasData: (WorkOrdersByPriority?.length ?? 0) > 0,
    });

  const [recentlyDroppedWorkOrder, setRecentlyDroppedWorkOrder] = useState<
    number | null
  >(null);
  const [showConcurrencyDialog, setShowConcurrencyDialog] = useState(false);
  const [concurrencyMessage, setConcurrencyMessage] = useState("");
  const [showConfig, setShowConfig] = useState(false);
  const zoomLevel = filters?.boardZoom ?? 1.0;

  const onDragEnd = async (result: DropResult) => {
    if (!result.destination) return;
    const { source, destination } = result;

    if (source.droppableId !== destination.droppableId) {
      const sourceColumn = columns[source.droppableId];
      const destColumn = columns[destination.droppableId];
      const sourceItems = [...sourceColumn.workOrders];
      const destItems = [...destColumn.workOrders];
      const [removed] = sourceItems.splice(source.index, 1);
      const updatedRemoved = {
        ...removed,
        woPriorityIdentity: destColumn.identity,
        woPriority: destColumn.displayText,
      };
      destItems.splice(destination.index, 0, updatedRemoved);

      const updatedSourceItems = sourceItems.map((item, index) => ({
        ...item,
        position: index,
        priorityPosition: index,
      }));
      const updatedDestItems = destItems.map((item, index) => ({
        ...item,
        position: index,
        priorityPosition: index,
      }));

      setColumns({
        ...columns,
        [source.droppableId]: { ...sourceColumn, workOrders: updatedSourceItems },
        [destination.droppableId]: { ...destColumn, workOrders: updatedDestItems },
      });

      setRecentlyDroppedWorkOrder(parseInt(result.draggableId, 10));

      const updatedWO: UpdateWorkOrderPriority = {
        invoiceDocumentIdentity: updatedRemoved.invoiceDocumentIdentity,
        woPriorityIdentity: destColumn.identity,
        modifiedDate: new Date().toISOString(),
        rowVersion: removed.rowVersion,
      };

      try {
        await WorkOrderQueueAPI.updateWorkOrderPriority(updatedWO);
      } catch (error) {
        if (error instanceof ConcurrencyError) {
          setConcurrencyMessage(
            "The work order has been updated by some other user, please refresh the board and try again!",
          );
          setShowConcurrencyDialog(true);
          return;
        }
        throw error;
      }

      await WorkOrderQueueAPI.updateWorkOrdersPriorityPositions({
        sourceItems: updatedSourceItems.map((item, index) => ({
          invoiceDocumentIdentity: item.invoiceDocumentIdentity,
          position: index,
        })),
        destItems: updatedDestItems.map((item, index) => ({
          invoiceDocumentIdentity: item.invoiceDocumentIdentity,
          position: index,
        })),
        items: null,
      });
    } else {
      const column = columns[source.droppableId];
      const copiedItems = [...column.workOrders];
      const [removed] = copiedItems.splice(source.index, 1);
      copiedItems.splice(destination.index, 0, removed);
      const updatedItems = copiedItems.map((workOrder, index) => ({
        ...workOrder,
        position: index,
        priorityPosition: index,
      }));

      setColumns((prev) => ({
        ...prev,
        [source.droppableId]: { ...column, workOrders: updatedItems },
      }));

      setRecentlyDroppedWorkOrder(parseInt(result.draggableId, 10));

      await WorkOrderQueueAPI.updateWorkOrdersPriorityPositions({
        items: updatedItems.map((item, index) => ({
          invoiceDocumentIdentity: item.invoiceDocumentIdentity,
          position: index,
        })),
        sourceItems: null,
        destItems: null,
      });
    }
  };

  const addNewWorkOrder = () => {
    showHideWorkOrderPopup(true);
  };

  return (
    <BoardLayout
      loading={loading}
      zoomLevel={zoomLevel}
      showConfig={showConfig}
      configPanel={
        <PriorityBoardCustomColumns
          columns={columns}
          onSave={(updated) => {
            setColumns(updated);
            setShowConfig(false);
          }}
          onCancel={() => setShowConfig(false)}
        />
      }
    >
      <DragDropContext onDragEnd={onDragEnd}>
        {Object.entries(columns)
          .filter(([, column]) => column.workOrders.length > 0)
          .sort(
            ([, a], [, b]) => (a.displayOrder ?? 0) - (b.displayOrder ?? 0),
          )
          .map(([columnId, column]) => (
            <Droppable key={columnId} droppableId={columnId}>
              {(provided) => (
                <Box
                  ref={provided.innerRef}
                  {...provided.droppableProps}
                  sx={{
                    flexGrow: 1,
                    flexBasis: Object.keys(columns).length > 4 ? "0" : "auto",
                    flexShrink: 0,
                    minWidth:
                      Object.keys(columns).length > 4
                        ? `${300 * zoomLevel}px`
                        : "0px",
                    maxWidth: `${300 * zoomLevel}px`,
                  }}
                >
                  <Card
                    elevation={0}
                    sx={{
                      borderRadius: 2,
                      bgcolor:
                        theme.palette.mode === "light" ? "#ebeced" : "#2d2d2d",
                      borderTop: `${8 * zoomLevel}px solid ${convertToRGBA(column.statusColor)}`,
                      borderTopLeftRadius: `${12 * zoomLevel}px`,
                      borderTopRightRadius: `${12 * zoomLevel}px`,
                      boxShadow: "0 2px 8px rgba(0,0,0,0.04)",
                      overflow: "visible",
                      height: "100%",
                    }}
                  >
                    <CardContent
                      className="priority"
                      sx={{
                        padding: `${9 * zoomLevel}px`,
                        textAlign: "right",
                        position: "sticky",
                        top: 0,
                        zIndex: 2,
                        backgroundColor:
                          theme.palette.mode === "light"
                            ? "#ebeced"
                            : "#2d2d2d",
                        borderRadius: `${12 * zoomLevel}px ${12 * zoomLevel}px 0 0`,
                        boxShadow: "0 2px 4px rgba(0,0,0,0.08)",
                      }}
                    >
                      <Box
                        display="flex"
                        alignItems="center"
                        sx={{
                          borderRadius: `${3 * zoomLevel}px`,
                          p: 0.3 * zoomLevel,
                        }}
                      >
                        <Chip
                          label=""
                          size="small"
                          sx={{
                            bgcolor: convertToRGBA(column.statusColor),
                            color: column.textColor,
                            mt: `${3 * zoomLevel}px`,
                            mr: `${5 * zoomLevel}px`,
                            width: `${12 * zoomLevel}px`,
                            height: `${12 * zoomLevel}px`,
                          }}
                        />
                        <Typography
                          sx={{
                            fontStyle: "normal",
                            fontWeight: "500",
                            fontSize: `${15 * zoomLevel}px`,
                            color: theme.palette.text.primary,
                          }}
                        >
                          {truncateName(column.displayText, 25)}
                        </Typography>
                        <Box flexGrow={1} />
                        <Tooltip title="Customize the column order">
                          <IconButton
                            onClick={() => setShowConfig(true)}
                            sx={{
                              marginLeft: `${3 * zoomLevel}px`,
                              padding: `${2 * zoomLevel}px`,
                              width: `${28 * zoomLevel}px`,
                              height: `${28 * zoomLevel}px`,
                              color: "#2371E7",
                              bgcolor: "#ffffff",
                              borderRadius: `${3 * zoomLevel}px`,
                              "&:hover": {
                                bgcolor:
                                  theme.palette.mode === "light"
                                    ? "rgba(0, 0, 0, 0.04)"
                                    : "rgba(255, 255, 255, 0.08)",
                              },
                            }}
                            size="small"
                          >
                            <Edit
                              sx={{
                                width: `${16 * zoomLevel}px`,
                                height: `${16 * zoomLevel}px`,
                                fontSize: `${15 * zoomLevel}px`,
                              }}
                            />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Add new work order">
                          <IconButton
                            onClick={addNewWorkOrder}
                            sx={{
                              marginLeft: `${3 * zoomLevel}px`,
                              padding: `${2 * zoomLevel}px`,
                              width: `${28 * zoomLevel}px`,
                              height: `${28 * zoomLevel}px`,
                              color: "#2371E7",
                              bgcolor: "#ffffff",
                              borderRadius: `${3 * zoomLevel}px`,
                              "&:hover": {
                                bgcolor:
                                  theme.palette.mode === "light"
                                    ? "rgba(0, 0, 0, 0.04)"
                                    : "rgba(255, 255, 255, 0.08)",
                              },
                            }}
                            size="small"
                          >
                            <AddIcon
                              sx={{
                                width: `${16 * zoomLevel}px`,
                                height: `${16 * zoomLevel}px`,
                                fontSize: `${16 * zoomLevel}px`,
                              }}
                            />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </CardContent>

                    {column.workOrders.map((workOrder, idx) => (
                      <Draggable
                        key={workOrder.invoiceDocumentIdentity}
                        draggableId={String(workOrder.invoiceDocumentIdentity)}
                        index={idx}
                      >
                        {(dragProvided) => (
                          <Box
                            ref={dragProvided.innerRef}
                            {...dragProvided.draggableProps}
                            {...dragProvided.dragHandleProps}
                            ml={zoomLevel}
                            mr={zoomLevel}
                            p={0.5 * zoomLevel}
                          >
                            <WorkOrderCard
                              index={idx}
                              color={column.statusColor}
                              boardView={BoardView.Priority}
                              workOrderObj={workOrder}
                              recentlyDroppedWorkOrder={
                                recentlyDroppedWorkOrder ?? undefined
                              }
                              isCollapsed={filters?.collapseWorkOrders ?? false}
                            />
                          </Box>
                        )}
                      </Draggable>
                    ))}
                  </Card>
                  {provided.placeholder}
                </Box>
              )}
            </Droppable>
          ))}
      </DragDropContext>

      {showDialog && (
        <WorkOrderDialogContainer
          showHideWoPopup={showHideWorkOrderPopup}
          InvoiceDocIdentity={selectedWorkOrderId || undefined}
          open={true}
          cacheNewWorkOrderIdOnSave
        />
      )}

      <ConcurrencyDialog
        open={showConcurrencyDialog}
        onDismiss={() => setShowConcurrencyDialog(false)}
        onRefresh={() => {
          setShowConcurrencyDialog(false);
          window.dispatchEvent(new CustomEvent("refreshWorkOrders"));
        }}
        message={concurrencyMessage}
      />
    </BoardLayout>
  );
});

export default PriorityBoard;
